
import React, { useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { ContaBancaria } from "@/types/financeiro";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getSafeNumber } from "./utils/numberUtils";
import SaldoHeader from "./components/SaldoHeader";
import SaldoActions from "./components/SaldoActions";
import ListaContas from "./components/ListaContas";

// Criando uma interface explícita para evitar tipos recursivos
type ContaBancariaDB = {
  id: string | null;
  banco: string | null;
  agencia: string | null;
  numero: string | null;
  descricao: string | null;
  saldo_inicial: number | null;
  saldo_atual: number | null;
  ativo: boolean | null;
  user_id: string | null;
  criado_em?: string | null;
  criado_por?: string | null;
  ultima_atualizacao?: string | null;
}

const SaldoSection: React.FC<{
  contas: ContaBancaria[];
  setContas: React.Dispatch<React.SetStateAction<ContaBancaria[]>>;
  adicionarConta: () => void;
}> = ({ contas, setContas, adicionarConta }) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const loadContas = async () => {
      try {
        if (!user) return;
        
        const { data, error } = await supabase
          .from('contas_bancarias')
          .select('*')
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        if (data && Array.isArray(data)) {
          // Usando for loop e tipos explícitos para evitar inferência recursiva profunda
          const contasBancarias: ContaBancaria[] = [];
          
          for (let i = 0; i < data.length; i++) {
            // Usando uma variável intermediária com tipo explícito
            const item = data[i] as unknown as ContaBancariaDB;
            contasBancarias.push({
              id: item.id || '',
              banco: item.banco || '',
              agencia: item.agencia || '',
              numero: item.numero || '',
              descricao: item.descricao || '',
              saldoInicial: getSafeNumber(item.saldo_inicial),
              saldoAtual: getSafeNumber(item.saldo_atual),
              ativo: Boolean(item.ativo)
            });
          }
          
          setContas(contasBancarias);
        }
      } catch (error) {
        console.error("Erro ao carregar contas bancárias:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar as contas bancárias.",
          duration: 3000,
        });
      }
    };
    
    loadContas();
  }, [setContas, toast, user]);

  const limparDados = async () => {
    try {
      if (!user) return;
      
      const { error } = await supabase
        .from('contas_bancarias')
        .delete()
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      setContas([]);
      toast({
        title: "Dados limpos",
        description: "Todas as contas bancárias foram removidas.",
        duration: 3000,
      });
    } catch (error) {
      console.error("Erro ao limpar dados:", error);
      toast({
        title: "Erro",
        description: "Não foi possível limpar os dados.",
        duration: 3000,
      });
    }
  };

  const salvarDados = async () => {
    if (!user) return;
    
    setIsSaving(true);
    
    try {
      // Processando cada conta com tipo explícito para o objeto a ser salvo
      for (const conta of contas) {
        // Definindo explicitamente o objeto para upsert para evitar problemas de tipo
        const contaParaSalvar: ContaBancariaDB = {
          id: conta.id,
          banco: conta.banco,
          agencia: conta.agencia,
          numero: conta.numero,
          descricao: conta.descricao,
          saldo_inicial: conta.saldoInicial,
          saldo_atual: conta.saldoAtual,
          ativo: conta.ativo,
          user_id: user.id
        };
        
        const { error } = await supabase
          .from('contas_bancarias')
          .upsert(contaParaSalvar);
        
        if (error) throw error;
      }
      
      toast({
        title: "Sucesso",
        description: "As informações das contas foram salvas.",
        duration: 3000,
      });
    } catch (error) {
      console.error("Erro ao salvar contas bancárias:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar as contas bancárias.",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saldoTotal = contas.reduce((total, conta) => total + conta.saldoAtual, 0);

  return (
    <div className="p-5">
      <SaldoHeader 
        saldoTotal={saldoTotal} 
        onSave={salvarDados} 
        isSaving={isSaving} 
      />
      
      <SaldoActions 
        onClear={limparDados} 
        isSaving={isSaving} 
      />
      
      <ListaContas 
        contas={contas} 
        adicionarConta={adicionarConta} 
        isSaving={isSaving} 
      />
    </div>
  );
};

export default SaldoSection;
